import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageTitle, useShop } from "@/context/ShopContext";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

export default function CollectionPage() {
  const { slug } = useParams();
  const { meta, products, loading } = useShop();
  const collection = (meta.collections || []).find((c) => c.slug === slug);
  usePageTitle(collection ? `${collection.name} Collection` : "Collection");
  const [heroOk, setHeroOk] = useState(true);

  const items = useMemo(
    () => products.filter((p) => p.collection_slug === slug && p.type === "single"),
    [products, slug]
  );

  const subFamilies = useMemo(() => {
    const order = (meta.families || []).map((f) => f.name);
    const groups = {};
    items.forEach((p) => {
      (groups[p.colour_family] = groups[p.colour_family] || []).push(p);
    });
    return order.filter((f) => groups[f]?.length).map((f) => ({ name: f, items: groups[f] }));
  }, [items, meta.families]);

  if (!loading && !collection) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-32 text-center" data-testid="collection-not-found">
        <p className="font-serif text-3xl">Collection not found</p>
        <Link to="/collections" className="btn-primary mt-8" data-testid="back-to-collections">All Collections</Link>
      </div>
    );
  }
  if (!collection) return null;

  return (
    <div data-testid={`collection-page-${slug}`}>
      <section className="relative min-h-[300px] h-[44vh] overflow-hidden bg-beige">
        {heroOk && (
          <img
            src={`/images/collections/${slug}.jpg`}
            alt={`The LAMSAH ${collection.name} collection`}
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setHeroOk(false)}
          />
        )}
        <div className="absolute inset-0 bg-espresso/40" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-8 flex items-end pb-14">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ivory/80 mb-4">
              {collection.fabric} · {collection.count} Shades
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-ivory" data-testid="collection-title">
              {collection.name.toUpperCase()}
            </h1>
            {collection.preorder && (
              <span className="inline-block bg-espresso text-ivory text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 mt-4" data-testid="collection-preorder-badge">
                Available on Pre-Order
              </span>
            )}
            {collection.friday_only && (
              <span className="inline-block bg-espresso text-ivory text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 mt-4 ml-2" data-testid="collection-friday-badge">
                {(() => { const d = (5 - new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })).getDay() + 7) % 7; return d === 0 ? "Open Today — Friday" : d === 1 ? "Opens Tomorrow — Friday" : `Opens Friday — in ${d} days`; })()}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20">
        <Reveal>
          <p className="text-sm sm:text-base text-espresso/75 leading-relaxed max-w-2xl" data-testid="collection-story">
            {collection.story}
          </p>
          <div className="flex flex-wrap gap-2 mt-8" data-testid="collection-swatches">
            {collection.shades.map((s) => (
              <span key={s.name + s.hex} className="w-7 h-7 rounded-full border border-beige" style={{ backgroundColor: s.hex }} title={`${s.name} — ${s.desc}`} />
            ))}
          </div>
          <Link
            to={`/shop?fabric=${encodeURIComponent(collection.fabric)}`}
            data-testid="collection-fabric-link"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-rose border-b border-rose/40 pb-0.5 mt-8 hover:text-espresso transition-colors"
          >
            About {collection.fabric}
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </Reveal>

        {subFamilies.map((group) => (
          <div key={group.name} className="mt-16 md:mt-20" data-testid={`subfamily-${group.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-baseline justify-between gap-4 mb-8">
              <h2 className="font-serif text-2xl sm:text-3xl font-light tracking-tight">{group.name}</h2>
              <p className="text-xs uppercase tracking-[0.2em] text-taupe">{group.items.length} {group.items.length === 1 ? "shade" : "shades"}</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
              {group.items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
